import { createPortal } from 'react-dom'
import { useDayFactor } from './useDayFactor'
import type { StormPhase } from '@/lib/storm/classify'
import './sky-tint.css'

/**
 * Full-page sun-driven background. Stacked full-viewport layers behind ALL content
 * (hero + chips + alerts + nav): a night base (almost-black purple, always on) and a day
 * layer (blue) crossfaded in by the day factor. It's `position: fixed` and portaled to
 * <body> so it sits at the lowest z-layer and covers the entire page — under scroll, with
 * no bounded box and no seam. The day layer's opacity eases 600ms so live ticks glide.
 *
 * THREAT TRANSFORM: when the location escalates, the whole page tint takes over —
 * WATCHING fades an amber wash OVER the day/night colour (the home reads elevated at a
 * glance, in place); STORM MODE on home reads red. Both ease in over 600ms.
 */
export function SkyTint({
  lat,
  lon,
  phase = 'quiet',
}: {
  lat: number | null
  lon: number | null
  phase?: StormPhase
}) {
  const factor = useDayFactor(lat, lon)
  if (typeof document === 'undefined') return null

  return createPortal(
    <div className="sky-tint" data-phase={phase} aria-hidden="true">
      <div className="sky-tint__base" />
      <div className="sky-tint__day" style={{ opacity: factor }} />
      <div className="sky-tint__watch" style={{ opacity: phase === 'watching' ? 1 : 0 }} />
      <div className="sky-tint__storm" style={{ opacity: phase === 'storm' ? 1 : 0 }} />
      <div className="sky-tint__grid" />
    </div>,
    document.body,
  )
}
